import type { PermissionDefinition } from '../types/permission.js';

// ─── System Permissions ─────────────────────────────────────────────
// Single source of truth for every permission in the system.
// Both frontend and backend import from here so permission keys are
// checked at compile time. The shape mirrors the Prisma Permission
// model (type + method + route) without DB-specific fields.
//
// Add new permissions here as they are introduced.

export const SystemPermissions = {
    // ── Identity Hub — Page ─────────────────────────────────────────
    IDENTITY_HUB_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'identity-hub' },

    // ── Identity Hub — Users ────────────────────────────────────────
    IDENTITY_HUB_LIST_USERS: { type: 'ACTION', method: 'GET', route: '/users' },
    IDENTITY_HUB_LIST_ALL_USERS: { type: 'ACTION', method: 'GET', route: '/users/all' },
    IDENTITY_HUB_USER_STATS: { type: 'ACTION', method: 'GET', route: '/users/stats' },
    IDENTITY_HUB_GET_USER: { type: 'ACTION', method: 'GET', route: '/users/:userId' },
    IDENTITY_HUB_GET_USER_ACCESS: { type: 'ACTION', method: 'GET', route: '/users/:userId/access' },
    IDENTITY_HUB_LIST_USERS_ACCESS: { type: 'ACTION', method: 'GET', route: '/users/access/batch' },
    IDENTITY_HUB_CREATE_USER: { type: 'ACTION', method: 'POST', route: '/users/register' },
    IDENTITY_HUB_UPDATE_USER: { type: 'ACTION', method: 'PUT', route: '/users/update' },
    IDENTITY_HUB_DEACTIVATE_USER: { type: 'ACTION', method: 'DELETE', route: '/users/deactivate' },
    IDENTITY_HUB_REACTIVATE_USER: { type: 'ACTION', method: 'POST', route: '/users/reactivate' },
    IDENTITY_HUB_GRANT_ACCESS:   { type: 'ACTION', method: 'POST', route: '/users/access/grant' },
    IDENTITY_HUB_REVOKE_ACCESS:  { type: 'ACTION', method: 'POST', route: '/users/access/revoke' },
    IDENTITY_HUB_BULK_GRANT_ACCESS:  { type: 'ACTION', method: 'POST', route: '/users/access/bulk-grant' },
    IDENTITY_HUB_BULK_REVOKE_ACCESS: { type: 'ACTION', method: 'POST', route: '/users/access/bulk-revoke' },
    IDENTITY_HUB_REPLACE_ACCESS: { type: 'ACTION', method: 'POST', route: '/users/access/replace' },

    // ── Identity Hub — Tenants ──────────────────────────────────────
    IDENTITY_HUB_LIST_TENANTS: { type: 'ACTION', method: 'GET', route: '/tenants' },
    IDENTITY_HUB_GET_TENANT: { type: 'ACTION', method: 'GET', route: '/tenants/:tenantId' },
    IDENTITY_HUB_CREATE_TENANT: { type: 'ACTION', method: 'POST', route: '/tenants/provision' },
    IDENTITY_HUB_UPDATE_TENANT: { type: 'ACTION', method: 'PUT', route: '/tenants/update' },
    IDENTITY_HUB_DEACTIVATE_TENANT: { type: 'ACTION', method: 'DELETE', route: '/tenants/deactivate' },
    /** Same scope as DEACTIVATE — deactivating + reactivating an org is one logical privilege. */
    IDENTITY_HUB_REACTIVATE_TENANT: { type: 'ACTION', method: 'POST', route: '/tenants/reactivate' },
    IDENTITY_HUB_IMPORT_TENANT_SETTINGS: { type: 'ACTION', method: 'POST', route: '/tenants/import-settings' },
    // ── Identity Hub — Access Groups ────────────────────────────────
    IDENTITY_HUB_LIST_ACCESS: { type: 'ACTION', method: 'GET', route: '/access/by-tenant/:tenantId' },
    IDENTITY_HUB_LIST_ACCESS_BATCH: { type: 'ACTION', method: 'GET', route: '/access/by-tenant/batch' },
    IDENTITY_HUB_LIST_ALL_ACCESS: { type: 'ACTION', method: 'GET', route: '/access/all' },
    IDENTITY_HUB_COUNT_ACCESS: { type: 'ACTION', method: 'GET', route: '/access/count' },
    IDENTITY_HUB_GET_ACCESS: { type: 'ACTION', method: 'GET', route: '/access/:accessId' },
    IDENTITY_HUB_CREATE_ACCESS: { type: 'ACTION', method: 'POST', route: '/access/create' },
    IDENTITY_HUB_UPDATE_ACCESS: { type: 'ACTION', method: 'PUT', route: '/access/update' },
    IDENTITY_HUB_DELETE_ACCESS: { type: 'ACTION', method: 'DELETE', route: '/access/delete' },
    /** Same scope as DELETE — deleting + restoring a role is one logical privilege. */
    IDENTITY_HUB_RESTORE_ACCESS: { type: 'ACTION', method: 'POST', route: '/access/restore' },
    IDENTITY_HUB_UPDATE_PERMISSIONS: { type: 'ACTION', method: 'PUT', route: '/access/permissions' },

    // ── Identity Hub — Invites ──────────────────────────────────────
    IDENTITY_HUB_LIST_INVITES:       { type: 'ACTION', method: 'GET',    route: '/invites' },
    IDENTITY_HUB_GENERATE_INVITE:    { type: 'ACTION', method: 'POST',   route: '/invites' },
    IDENTITY_HUB_BULK_INVITE:        { type: 'ACTION', method: 'POST',   route: '/invites/bulk' },
    IDENTITY_HUB_REGENERATE_INVITE:  { type: 'ACTION', method: 'POST',   route: '/invites/regenerate' },
    IDENTITY_HUB_RESEND_INVITE:      { type: 'ACTION', method: 'POST',   route: '/invites/:inviteId/resend' },
    IDENTITY_HUB_GET_INVITE_LINK:    { type: 'ACTION', method: 'GET',    route: '/invites/:inviteId/link' },
    IDENTITY_HUB_REVOKE_INVITE:      { type: 'ACTION', method: 'DELETE', route: '/invites/:inviteId' },

    // ── Identity Hub — Uploads ──────────────────────────────────────
    IDENTITY_HUB_UPLOAD_TENANT_LOGO_PRESIGN:      { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    IDENTITY_HUB_UPLOAD_TENANT_LOGO_CONFIRM:      { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    IDENTITY_HUB_UPLOAD_TENANT_LOGO_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },
    IDENTITY_HUB_UPLOAD_USER_AVATAR_PRESIGN:      { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    IDENTITY_HUB_UPLOAD_USER_AVATAR_CONFIRM:      { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    IDENTITY_HUB_UPLOAD_USER_AVATAR_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },

    // ── Identity Hub — Audit Logs ───────────────────────────────────
    IDENTITY_HUB_VIEW_AUDIT_LOG:        { type: 'ACTION', method: 'GET', route: '/audit-logs' },
    IDENTITY_HUB_VIEW_USER_AUDIT_LOG:   { type: 'ACTION', method: 'GET', route: '/audit-logs/users/:userId' },
    IDENTITY_HUB_VIEW_ACCESS_AUDIT_LOG: { type: 'ACTION', method: 'GET', route: '/audit-logs/access/:accessId' },
    IDENTITY_HUB_VIEW_TENANT_AUDIT_LOG: { type: 'ACTION', method: 'GET', route: '/audit-logs/tenants/:tenantId' },

    // ── Admin ───────────────────────────────────────────────────────
    ADMIN_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'admin' },
    ADMIN_LIST_EVENT_JOBS: { type: 'ACTION', method: 'GET', route: '/admin/events' },

    // ── Admin — Changelog ───────────────────────────────────────────
    // Split deliberately: drafting release notes and deciding they go out to
    // every user, a Slack channel, and the team inbox are different levels of
    // trust, so the write keys and the approve/send keys are granted apart.
    ADMIN_LIST_CHANGELOG:            { type: 'ACTION', method: 'GET',    route: '/admin/changelog' },
    ADMIN_SYNC_CHANGELOG:            { type: 'ACTION', method: 'POST',   route: '/admin/changelog/sync' },
    ADMIN_REGENERATE_CHANGELOG:      { type: 'ACTION', method: 'POST',   route: '/admin/changelog/:versionTag/regenerate' },
    ADMIN_EDIT_CHANGELOG:            { type: 'ACTION', method: 'PUT',    route: '/admin/changelog/:versionTag' },
    ADMIN_APPROVE_CHANGELOG:         { type: 'ACTION', method: 'POST',   route: '/admin/changelog/:versionTag/approve' },
    ADMIN_REVOKE_CHANGELOG_APPROVAL: { type: 'ACTION', method: 'DELETE', route: '/admin/changelog/:versionTag/approval' },
    ADMIN_SEND_CHANGELOG:            { type: 'ACTION', method: 'POST',   route: '/admin/changelog/:versionTag/send' },
    ADMIN_LIST_CHANGELOG_RECIPIENTS: { type: 'ACTION', method: 'GET',    route: '/admin/changelog/recipients' },
    ADMIN_ADD_CHANGELOG_RECIPIENT:   { type: 'ACTION', method: 'POST',   route: '/admin/changelog/recipients' },
    // Keyed on the address rather than a row id — a team member who has never
    // been switched off has no row yet, and this request is what creates one.
    ADMIN_UPDATE_CHANGELOG_RECIPIENT:{ type: 'ACTION', method: 'PATCH',  route: '/admin/changelog/recipients' },
    ADMIN_REMOVE_CHANGELOG_RECIPIENT:{ type: 'ACTION', method: 'DELETE', route: '/admin/changelog/recipients/:recipientId' },
    // Reading how the changelog behaves is a listing-level grant; changing it
    // decides whether anything reaches Slack without a human, so it sits with
    // the approve and send keys rather than with the drafting ones.
    ADMIN_GET_CHANGELOG_SETTINGS:    { type: 'ACTION', method: 'GET',    route: '/admin/changelog/settings' },
    ADMIN_UPDATE_CHANGELOG_SETTINGS: { type: 'ACTION', method: 'PUT',    route: '/admin/changelog/settings' },
    // Posts a throwaway message so an admin can prove the webhook works
    // before trusting it with unattended posts. Sits with the settings keys:
    // it is a configuration check, not a release action.
    ADMIN_TEST_CHANGELOG_SLACK:      { type: 'ACTION', method: 'POST',   route: '/admin/changelog/slack-test' },
    // The same check against the other webhook. A separate key because the two
    // channels are separately configured, and proving one reaches Slack is not
    // permission to post into the other.
    ADMIN_TEST_ALERTS_SLACK:         { type: 'ACTION', method: 'POST',   route: '/admin/changelog/alerts-slack-test' },

    // ── Admin — Changelog digests ───────────────────────────────────
    // The same draft/approve split as entries. Digests are what actually reach
    // the mailing list and the in-app banner, so if anything the approve key
    // here is the more consequential of the two.
    ADMIN_LIST_CHANGELOG_DIGESTS:    { type: 'ACTION', method: 'GET',    route: '/admin/changelog/digests' },
    ADMIN_COMPOSE_CHANGELOG_DIGEST:  { type: 'ACTION', method: 'POST',   route: '/admin/changelog/digests' },
    ADMIN_LIST_DIGEST_CANDIDATES:    { type: 'ACTION', method: 'GET',    route: '/admin/changelog/digests/candidates' },
    ADMIN_EDIT_CHANGELOG_DIGEST:     { type: 'ACTION', method: 'PUT',    route: '/admin/changelog/digests/:digestId' },
    ADMIN_DELETE_CHANGELOG_DIGEST:   { type: 'ACTION', method: 'DELETE', route: '/admin/changelog/digests/:digestId' },
    ADMIN_REGENERATE_DIGEST:         { type: 'ACTION', method: 'POST',   route: '/admin/changelog/digests/:digestId/regenerate' },
    ADMIN_APPROVE_CHANGELOG_DIGEST:  { type: 'ACTION', method: 'POST',   route: '/admin/changelog/digests/:digestId/approve' },
    ADMIN_REVOKE_DIGEST_APPROVAL:    { type: 'ACTION', method: 'DELETE', route: '/admin/changelog/digests/:digestId/approval' },
    ADMIN_SEND_CHANGELOG_DIGEST:     { type: 'ACTION', method: 'POST',   route: '/admin/changelog/digests/:digestId/send' },

    // ── Admin — Operations settings ─────────────────────────────────
    // How the Events and Scheduled jobs tabs behave: retention, defaults,
    // stuck thresholds, failure alerts. Reading is a listing-level grant;
    // writing decides whether event history is deleted and whether a Slack
    // channel gets paged, so it is a separate key.
    ADMIN_GET_OPS_SETTINGS:    { type: 'ACTION', method: 'GET', route: '/admin/settings/ops' },
    ADMIN_UPDATE_OPS_SETTINGS: { type: 'ACTION', method: 'PUT', route: '/admin/settings/ops' },

    // ── Dashboard ───────────────────────────────────────────────────
    DASHBOARD_VIEW_PAGE:   { type: 'PAGE',   method: 'GET', route: 'dashboard' },
    DASHBOARD_GET_SUMMARY: { type: 'ACTION', method: 'GET', route: '/dashboard/summary' },
    DASHBOARD_GET_MY_BRAND_CHART:          { type: 'ACTION', method: 'GET', route: '/dashboard/my-brand-chart' },
    DASHBOARD_GET_COMPETITOR_CHART:        { type: 'ACTION', method: 'GET', route: '/dashboard/competitor-chart' },
    DASHBOARD_GET_MY_BRAND_FILTER_OPTIONS: { type: 'ACTION', method: 'GET', route: '/dashboard/my-brand-filter-options' },
    DASHBOARD_GET_COMPETITOR_FILTER_DEFAULTS: { type: 'ACTION', method: 'GET', route: '/dashboard/competitor-filter-defaults' },
    DASHBOARD_PUT_COMPETITOR_FILTER_DEFAULTS: { type: 'ACTION', method: 'PUT', route: '/dashboard/competitor-filter-defaults' },
    DASHBOARD_INDUSTRIES_LIST: { type: 'ACTION', method: 'GET', route: '/dashboard/industries' },
    DASHBOARD_INDUSTRY_PRODUCTS: { type: 'ACTION', method: 'GET', route: '/dashboard/industry-products' },
    DASHBOARD_COMPETITOR_OPTIONS: { type: 'ACTION', method: 'GET', route: '/dashboard/competitor-options' },

    // ── Workspace ───────────────────────────────────────────────────
    WORKSPACE_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'workspace' },

    // ── Personas (Persona Library) ──────────────────────────────────
    PERSONAS_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'personas' },
    PERSONAS_LIST: { type: 'ACTION', method: 'GET', route: '/personas/list' },
    PERSONAS_GET: { type: 'ACTION', method: 'GET', route: '/personas/:id' },
    // Separate from PERSONAS_GET: usage names the opportunity sets a persona
    // feeds, which is persona-opportunities data surfaced on the Personas page.
    // Reading a persona's profile must not imply seeing the tenant's set names.
    PERSONAS_GET_USAGE: { type: 'ACTION', method: 'GET', route: '/personas/:id/usage' },
    PERSONAS_DISCOVER: { type: 'ACTION', method: 'POST', route: '/personas/discover' },
    PERSONAS_REMOVE: { type: 'ACTION', method: 'DELETE', route: '/personas/:id' },
    PERSONAS_REJECT: { type: 'ACTION', method: 'POST', route: '/personas/:id/reject' },
    PERSONAS_STATUS: { type: 'ACTION', method: 'GET', route: '/personas/status/:correlationId' },
    PERSONAS_ONBOARDING_STATUS: { type: 'ACTION', method: 'GET', route: '/personas/onboarding/status' },
    PERSONAS_ONBOARDING_SUGGESTIONS: { type: 'ACTION', method: 'GET', route: '/personas/onboarding/suggestions' },
    PERSONAS_ONBOARDING_ACCEPT: { type: 'ACTION', method: 'POST', route: '/personas/onboarding/accept' },
    PERSONAS_ONBOARDING_REJECT: { type: 'ACTION', method: 'POST', route: '/personas/onboarding/reject' },
    PERSONAS_ONBOARDING_UNDO: { type: 'ACTION', method: 'POST', route: '/personas/onboarding/undo' },
    PERSONAS_ONBOARDING_CONTINUE: { type: 'ACTION', method: 'POST', route: '/personas/onboarding/continue' },
    // Server-generated avatars (A2) reuse the shared upload routes; no client presign UI yet.
    PERSONAS_UPLOAD_AVATAR_PRESIGN:      { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    PERSONAS_UPLOAD_AVATAR_CONFIRM:      { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    PERSONAS_UPLOAD_AVATAR_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },

    // ── Marketplace ─────────────────────────────────────────────────
    MARKETPLACE_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'marketplace' },
    MARKETPLACE_LIST_VENDORS: { type: 'ACTION', method: 'GET', route: '/marketplace/vendors' },
    MARKETPLACE_GET_VENDOR_DETAILS: { type: 'ACTION', method: 'GET', route: '/marketplace/vendors/:vendorId/questions' },
    MARKETPLACE_LIST_CONNECTIONS: { type: 'ACTION', method: 'GET', route: '/marketplace/connections' },
    MARKETPLACE_CREATE_CONNECTION: { type: 'ACTION', method: 'POST', route: '/marketplace/connections' },
    MARKETPLACE_UPDATE_CONNECTION_ANSWERS: { type: 'ACTION', method: 'PUT', route: '/marketplace/connections/:id/answers' },
    MARKETPLACE_RESIGN_CONNECTION: { type: 'ACTION', method: 'POST', route: '/marketplace/connections/:id/resign' },
    MARKETPLACE_DISCONNECT_VENDOR: { type: 'ACTION', method: 'DELETE', route: '/marketplace/connections/:id' },
    MARKETPLACE_LIST_ORDERS: { type: 'ACTION', method: 'GET', route: '/marketplace/orders' },
    MARKETPLACE_LIST_SENT_SCRIPTS: { type: 'ACTION', method: 'GET', route: '/marketplace/orders/sent-scripts' },
    MARKETPLACE_CREATE_ORDER: { type: 'ACTION', method: 'POST', route: '/marketplace/orders' },
    MARKETPLACE_RETRY_ORDER: { type: 'ACTION', method: 'POST', route: '/marketplace/orders/:id/retry' },
    MARKETPLACE_UPDATE_ORDER_ETA: { type: 'ACTION', method: 'PUT', route: '/marketplace/orders/:id/eta' },

    // ── Marketplace Admin ────────────────────────────────────────────
    MARKETPLACE_ADMIN_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'marketplace.admin' },
    MARKETPLACE_ADMIN_LIST_VENDORS: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/vendors' },
    MARKETPLACE_ADMIN_GET_VENDOR: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/vendors/:vendorId' },
    MARKETPLACE_ADMIN_CREATE_VENDOR: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors' },
    MARKETPLACE_ADMIN_REPLACE_VENDOR: { type: 'ACTION', method: 'PUT', route: '/marketplace/admin/vendors/:vendorId' },
    MARKETPLACE_ADMIN_ACTIVATE_VENDOR: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/:vendorId/activate' },
    MARKETPLACE_ADMIN_DEACTIVATE_VENDOR: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/:vendorId/deactivate' },
    // Approving an address makes it an egress target, so it is additionally
    // gated by requireSuperTenant on the route itself.
    MARKETPLACE_ADMIN_APPROVE_CONTACT_EMAIL: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/:vendorId/contact-emails/:emailId/approve' },
    MARKETPLACE_ADMIN_REVOKE_CONTACT_EMAIL: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/:vendorId/contact-emails/:emailId/revoke' },
    MARKETPLACE_ADMIN_LIST_TEAM_RECIPIENTS: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/team-recipients' },
    MARKETPLACE_ADMIN_CREATE_TEAM_RECIPIENT: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/team-recipients' },
    MARKETPLACE_ADMIN_UPDATE_TEAM_RECIPIENT: { type: 'ACTION', method: 'PUT', route: '/marketplace/admin/team-recipients/:recipientId' },
    MARKETPLACE_ADMIN_DELETE_TEAM_RECIPIENT: { type: 'ACTION', method: 'DELETE', route: '/marketplace/admin/team-recipients/:recipientId' },
    MARKETPLACE_ADMIN_UPLOAD_LOGO_PRESIGN: { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    MARKETPLACE_ADMIN_UPLOAD_LOGO_CONFIRM: { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    MARKETPLACE_ADMIN_UPLOAD_LOGO_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },

    // ── Overview ────────────────────────────────────────────────────
    OVERVIEW_VIEW_PAGE:   { type: 'PAGE',   method: 'GET', route: 'overview' },
    OVERVIEW_GET_SUMMARY: { type: 'ACTION', method: 'GET', route: '/overview/summary' },
    OVERVIEW_SCENARIO_OPTIONS: { type: 'ACTION', method: 'GET', route: '/overview/scenario-options' },
    OVERVIEW_SUBFEATURE_OPTIONS: { type: 'ACTION', method: 'GET', route: '/overview/subfeature-options' },
    OVERVIEW_GROUP_BY_OPTIONS: { type: 'ACTION', method: 'GET', route: '/overview/group-by-options' },
    OVERVIEW_GET_COMPETITOR_FILTER_DEFAULTS: { type: 'ACTION', method: 'GET', route: '/overview/competitor-filter-defaults' },
    OVERVIEW_PUT_COMPETITOR_FILTER_DEFAULTS: { type: 'ACTION', method: 'PUT', route: '/overview/competitor-filter-defaults' },
    OVERVIEW_INDUSTRIES_LIST: { type: 'ACTION', method: 'GET', route: '/overview/industries' },
    OVERVIEW_INDUSTRY_PRODUCTS: { type: 'ACTION', method: 'GET', route: '/overview/industry-products' },
    OVERVIEW_COMPETITOR_OPTIONS: { type: 'ACTION', method: 'GET', route: '/overview/competitor-options' },
    OVERVIEW_TOPIC_DETAIL_EXAMPLES: { type: 'ACTION', method: 'GET', route: '/overview/topic-detail/examples' },
    OVERVIEW_TOPIC_DETAIL_FATIGUE: { type: 'ACTION', method: 'GET', route: '/overview/topic-detail/fatigue' },
    OVERVIEW_TOPIC_DETAIL_COMP_EXAMPLES: { type: 'ACTION', method: 'GET', route: '/overview/topic-detail/comp/examples' },
    OVERVIEW_TOPIC_DETAIL_COMP_FATIGUE: { type: 'ACTION', method: 'GET', route: '/overview/topic-detail/comp/fatigue' },

    // ── Drilldown ───────────────────────────────────────────────────
    DRILLDOWN_VIEW_PAGE: { type: 'PAGE',   method: 'GET', route: 'drilldown' },
    DRILLDOWN_GET_DATA:  { type: 'ACTION', method: 'GET', route: '/drilldown/data' },
    DRILLDOWN_GET_CREATIVE_HASH_DATA: { type: 'ACTION', method: 'GET', route: '/drilldown/creative-hash-data' },
    DRILLDOWN_GET_MY_BRAND_CREATIVE_DATA: { type: 'ACTION', method: 'GET', route: '/drilldown/my-brand-creative-data' },
    DRILLDOWN_GET_MY_BRAND_CREATIVE_PERFORMANCE: { type: 'ACTION', method: 'GET', route: '/drilldown/my-brand-creative-performance' },
    DRILLDOWN_GET_MY_BRAND_CREATIVE_FATIGUE: { type: 'ACTION', method: 'GET', route: '/drilldown/my-brand-creative-fatigue' },
    DRILLDOWN_GET_COMPETITOR_FILTER_DEFAULTS: { type: 'ACTION', method: 'GET', route: '/drilldown/competitor-filter-defaults' },
    DRILLDOWN_PUT_COMPETITOR_FILTER_DEFAULTS: { type: 'ACTION', method: 'PUT', route: '/drilldown/competitor-filter-defaults' },
    DRILLDOWN_INDUSTRIES_LIST: { type: 'ACTION', method: 'GET', route: '/drilldown/industries' },
    DRILLDOWN_INDUSTRY_PRODUCTS: { type: 'ACTION', method: 'GET', route: '/drilldown/industry-products' },
    DRILLDOWN_COMPETITOR_OPTIONS: { type: 'ACTION', method: 'GET', route: '/drilldown/competitor-options' },

    // ── Opportunities ───────────────────────────────────────────────
    OPPORTUNITIES_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'opportunities' },
    OPPORTUNITIES_LIST: { type: 'ACTION', method: 'GET', route: '/opportunities/list' },
    OPPORTUNITIES_FACETS: { type: 'ACTION', method: 'GET', route: '/opportunities/facets' },
    OPPORTUNITIES_CREATIVES: { type: 'ACTION', method: 'GET', route: '/opportunities/creatives' },
    OPPORTUNITIES_GENERATE_NEW_SET: { type: 'ACTION', method: 'POST', route: '/opportunities/generate-new-set' },
    OPPORTUNITIES_GENERATE: { type: 'ACTION', method: 'POST', route: '/opportunities/generate' },
    OPPORTUNITIES_REJECT: { type: 'ACTION', method: 'POST', route: '/opportunities/reject' },
    OPPORTUNITIES_EXPORT: { type: 'ACTION', method: 'POST', route: '/opportunities/export' },
    OPPORTUNITIES_STATUS: { type: 'ACTION', method: 'GET', route: '/opportunities/status/:correlationId' },
    OPPORTUNITIES_CANCEL: { type: 'ACTION', method: 'POST', route: '/opportunities/cancel' },
    OPPORTUNITIES_INDUSTRIES_LIST: { type: 'ACTION', method: 'GET', route: '/opportunities/industries' },
    OPPORTUNITIES_INDUSTRY_PRODUCTS: { type: 'ACTION', method: 'GET', route: '/opportunities/industry-products' },
    OPPORTUNITIES_TENANT_PRODUCTS: { type: 'ACTION', method: 'GET', route: '/opportunities/tenant-products' },
    OPPORTUNITIES_TENANT_PRODUCT_TYPES: { type: 'ACTION', method: 'GET', route: '/opportunities/tenant-product-types' },
    OPPORTUNITIES_COMPETITOR_OPTIONS: { type: 'ACTION', method: 'GET', route: '/opportunities/competitor-options' },

    // ── Opportunity Sets ──────────────────────────────────────────────
    OPPORTUNITY_SETS_LIST: { type: 'ACTION', method: 'GET', route: '/opportunity-sets/list' },
    OPPORTUNITY_SETS_CREATE: { type: 'ACTION', method: 'POST', route: '/opportunity-sets' },
    OPPORTUNITY_SETS_RENAME: { type: 'ACTION', method: 'PATCH', route: '/opportunity-sets/:id' },
    OPPORTUNITY_SETS_DELETE: { type: 'ACTION', method: 'DELETE', route: '/opportunity-sets/:id' },
    OPPORTUNITY_SETS_RETRY: { type: 'ACTION', method: 'POST', route: '/opportunity-sets/:id/retry' },
    OPPORTUNITY_SETS_MINIMAL: { type: 'ACTION', method: 'POST', route: '/opportunity-sets/minimal-generation' },

    // ── Opportunity Sets — Completed Scripts ──────────────────────────
    OPPORTUNITY_SETS_SCRIPTS_LIST:           { type: 'ACTION', method: 'GET',   route: '/opportunity-sets/:id/scripts' },
    OPPORTUNITY_SETS_SCRIPTS_FACETS:         { type: 'ACTION', method: 'GET',   route: '/opportunity-sets/:id/scripts/facets' },
    OPPORTUNITY_SETS_SCRIPTS_EXPORT:         { type: 'ACTION', method: 'GET',   route: '/opportunity-sets/:id/scripts/export.xlsx' },
    OPPORTUNITY_SETS_SCRIPTS_EXPORT_BY_IDS:  { type: 'ACTION', method: 'POST',  route: '/opportunity-sets/:id/scripts/export.xlsx' },
    OPPORTUNITY_SETS_SCRIPTS_REVERT:         { type: 'ACTION', method: 'PATCH', route: '/opportunity-sets/:id/scripts/:scriptId/revert' },
    OPPORTUNITY_SETS_SCRIPTS_FAVORITE:       { type: 'ACTION', method: 'PATCH', route: '/opportunity-sets/:id/scripts/:scriptId/favorite' },
    OPPORTUNITY_SETS_SCRIPTS_REORDER_SCENES: { type: 'ACTION', method: 'PATCH', route: '/opportunity-sets/:id/scripts/:scriptId/scenes/reorder' },
    OPPORTUNITY_SETS_SCRIPTS_AI_EDIT:        { type: 'ACTION', method: 'POST',  route: '/opportunity-sets/:id/scripts/ai-edit' },
    OPPORTUNITY_SETS_SCRIPTS_SUGGEST:        { type: 'ACTION', method: 'POST',  route: '/opportunity-sets/:id/scripts/suggest' },
    OPPORTUNITY_SETS_SCRIPTS_RESOLVE_LINKS:  { type: 'ACTION', method: 'POST',  route: '/opportunity-sets/:id/scripts/resolve-links' },
    OPPORTUNITIES_SCRIPT_STORYBOARD_GENERATE: { type: 'ACTION', method: 'POST', route: '/opportunity-sets/:id/scripts/:scriptId/storyboard' },

    // ── Persona Opportunities (own PAGE — not legacy Opportunities) ───
    PERSONA_OPPORTUNITIES_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'persona-opportunities' },
    // Read bundle — list, detail, and Amber status. Replaces the former
    // PERSONA_OPPORTUNITY_SETS_USE bundle; same route, so existing grants keep read access.
    PERSONA_OPPORTUNITY_SETS_LIST: { type: 'ACTION', method: 'GET', route: '/persona-opportunity-sets' },
    PERSONA_OPPORTUNITY_SETS_GENERATE: { type: 'ACTION', method: 'POST', route: '/persona-opportunity-sets/generate' },
    // Capability permission (no mounted route). Checked in POST /generate when `advanced` is sent.
    PERSONA_OPPORTUNITY_SETS_QUERY_TUNING: {
        type: 'ACTION',
        method: 'POST',
        route: '/persona-opportunity-sets/generate/query-tuning',
    },
    PERSONA_OPPORTUNITY_SETS_REFERENCE_VIDEO_PRESIGN: {
        type: 'ACTION',
        method: 'POST',
        route: '/uploads/presign',
    },
    PERSONA_OPPORTUNITY_SETS_REFERENCE_VIDEO_CONFIRM: {
        type: 'ACTION',
        method: 'POST',
        route: '/uploads/confirm',
    },
    PERSONA_OPPORTUNITY_SETS_GUIDELINES_EXTRACT: {
        type: 'ACTION',
        method: 'POST',
        route: '/persona-opportunity-sets/:id/guidelines/extract',
    },

    // ── Pre Flight ──────────────────────────────────────────────────────
    // Enforcement is per-route (`requirePermission` matches method + routeOptions.url
    // exactly, KTD8). The VIEW / GENERATE / MANAGE comment buckets are *grant bundles*
    // over these per-route keys — a role is granted a whole bucket, but every route
    // still carries its own key so a missing/ drifted key fails closed (Security #4).
    PREFLIGHT_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'pre-flight' },

    // VIEW — read the catalogue, reports, and live status
    PREFLIGHT_GET_REPORT:    { type: 'ACTION', method: 'GET', route: '/preflight/reports/:id' },
    PREFLIGHT_LIST_REPORTS:  { type: 'ACTION', method: 'GET', route: '/preflight/reports' },
    PREFLIGHT_REPORT_STATUS: { type: 'ACTION', method: 'GET', route: '/preflight/reports/:id/status' },
    PREFLIGHT_GET_CRITERIA:  { type: 'ACTION', method: 'GET', route: '/preflight/criteria' },
    PREFLIGHT_EXPORT_PDF:    { type: 'ACTION', method: 'GET', route: '/preflight/reports/:id/export.pdf' },
    PREFLIGHT_LIST_MARKETS:  { type: 'ACTION', method: 'GET', route: '/preflight/markets' },
    PREFLIGHT_GET_INSIGHTS:  { type: 'ACTION', method: 'GET', route: '/preflight/insights' },
    PREFLIGHT_GET_CREATIVE_PREVIEWS: { type: 'ACTION', method: 'GET', route: '/preflight/creative-previews' },
    // Starring is a per-user preference, not a shared edit — it sits with VIEW so
    // anyone who can read the list can curate their own favorites.
    PREFLIGHT_FAVORITE_REPORT: { type: 'ACTION', method: 'PATCH', route: '/preflight/reports/:id/favorite' },

    // GENERATE — upload assets, create a report, re-run analysis
    PREFLIGHT_UPLOAD_PRESIGN:      { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    PREFLIGHT_UPLOAD_CONFIRM:      { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    PREFLIGHT_UPLOAD_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },
    PREFLIGHT_CREATE_REPORT:       { type: 'ACTION', method: 'POST', route: '/preflight/reports' },
    PREFLIGHT_RETRY_REPORT:        { type: 'ACTION', method: 'POST', route: '/preflight/reports/:id/retry' },

    // MANAGE — rename / reassign market / soft-delete any report in the tenant
    // (no ownership gate), and curate the tenant's controlled market list.
    // Rename and market reassignment share this PATCH route.
    PREFLIGHT_RENAME_REPORT: { type: 'ACTION', method: 'PATCH',  route: '/preflight/reports/:id' },
    PREFLIGHT_DELETE_REPORT: { type: 'ACTION', method: 'DELETE', route: '/preflight/reports/:id' },
    PREFLIGHT_CREATE_MARKET: { type: 'ACTION', method: 'POST',   route: '/preflight/markets' },
    PREFLIGHT_DELETE_MARKET: { type: 'ACTION', method: 'DELETE', route: '/preflight/markets/:id' },

    // ── Pre Flight – Scripts (script-review) ─────────────────────────
    // Standard grantable permissions — surfaced in the Identity Hub
    // permission-set editor and seeded into every org's Admins group.
    // `requirePermission` auto-passes SUPER_TENANT (Adge admins keep
    // cross-org access). The upload policy references the presign/confirm
    // keys. Distinct from PREFLIGHT (asset scoring).
    SCRIPT_REVIEW_VIEW_PAGE:           { type: 'PAGE',   method: 'GET',    route: 'pre-flight-scripts' },
    SCRIPT_REVIEW_UPLOAD_PRESIGN:      { type: 'ACTION', method: 'POST',   route: '/uploads/presign' },
    SCRIPT_REVIEW_UPLOAD_CONFIRM:      { type: 'ACTION', method: 'POST',   route: '/uploads/confirm' },
    SCRIPT_REVIEW_UPLOAD_STAGE_MEMORY: { type: 'ACTION', method: 'POST',   route: '/uploads/stage-memory' },
    SCRIPT_REVIEW_CREATE:              { type: 'ACTION', method: 'POST',   route: '/script-reviews' },
    SCRIPT_REVIEW_LIST:                { type: 'ACTION', method: 'GET',    route: '/script-reviews' },
    SCRIPT_REVIEW_GET:                 { type: 'ACTION', method: 'GET',    route: '/script-reviews/:id' },
    SCRIPT_REVIEW_GET_STATUS:          { type: 'ACTION', method: 'GET',    route: '/script-reviews/:id/status' },
    SCRIPT_REVIEW_POST_NOTE:           { type: 'ACTION', method: 'POST',   route: '/script-reviews/:id/notes' },
    SCRIPT_REVIEW_CREATE_SHARE:        { type: 'ACTION', method: 'POST',   route: '/script-reviews/:id/share' },
    SCRIPT_REVIEW_REVOKE_SHARE:        { type: 'ACTION', method: 'DELETE', route: '/script-reviews/:id/share' },

    // ── MCP (remote Model Context Protocol provider) ─────────────────
    // Master switch for services/mcp. Not a web page — the PAGE type is the
    // existing "capability, not route" shape, and MCP tools additionally
    // require their own ACTION key. Never auto-seeded (see
    // MANUAL_GRANT_ONLY_ROUTES in packages/db/src/seed/all-permissions.ts).
    MCP_ACCESS: { type: 'PAGE', method: 'GET', route: 'mcp' },
    // Per-tool ACTION keys. Tool calls arrive as JSON-RPC over POST /mcp, so
    // `/mcp/tools/<tool_name>` is a capability route with no Fastify mount —
    // the tool registry checks it programmatically. Every tool owns its key
    // here rather than borrowing the source page's, so the `mcp` domain stays
    // the single owner of what a chat client can reach.
    MCP_TOOL_LIST_RECENT_PERSONA_OPPORTUNITIES: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/list_recent_persona_opportunities',
    },
    MCP_TOOL_LIST_INSIGHT_SLICES: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/list_insight_slices',
    },
    MCP_TOOL_LIST_FILTER_OPTIONS: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/list_filter_options',
    },
    MCP_TOOL_GET_COMPETITIVE_INSIGHTS: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_competitive_insights',
    },
    MCP_TOOL_GET_BRAND_PERFORMANCE: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_brand_performance',
    },
    MCP_TOOL_GET_INDUSTRY_OVERVIEW: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_industry_overview',
    },
    MCP_TOOL_LIST_PERSONAS: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/list_personas',
    },

    // ── Public API (Pre Flight scoring on services/api) ────────────
    // Master switch for /public routes (and host-gated dedicated Public API DNS). Not a web page —
    // capability route like MCP. RBAC maps to existing PREFLIGHT_* ACTION keys.
    PUBLIC_API_ACCESS: { type: 'PAGE', method: 'GET', route: 'public-api' },
    PUBLIC_API_LIST_TOKENS: { type: 'ACTION', method: 'GET', route: '/api-tokens' },
    PUBLIC_API_CREATE_TOKEN: { type: 'ACTION', method: 'POST', route: '/api-tokens' },
    PUBLIC_API_UPDATE_TOKEN: { type: 'ACTION', method: 'PATCH', route: '/api-tokens/:id' },
    PUBLIC_API_DELETE_TOKEN: { type: 'ACTION', method: 'DELETE', route: '/api-tokens/:id' },

    // ── Prompts ──────────────────────────────────────────────────────
    PROMPTS_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'prompts' },
    PROMPTS_LIST: { type: 'ACTION', method: 'GET', route: '/prompts' },
    PROMPTS_GET: { type: 'ACTION', method: 'GET', route: '/prompts/:id' },
    PROMPTS_CREATE: { type: 'ACTION', method: 'POST', route: '/prompts' },
    PROMPTS_UPDATE: { type: 'ACTION', method: 'PATCH', route: '/prompts/:id' },
    PROMPTS_DELETE: { type: 'ACTION', method: 'DELETE', route: '/prompts/:id' },
    PROMPTS_CREATE_VERSION: { type: 'ACTION', method: 'POST', route: '/prompts/:id/versions' },
    PROMPTS_LIST_VERSIONS: { type: 'ACTION', method: 'GET', route: '/prompts/:id/versions' },
    PROMPTS_REVERT: { type: 'ACTION', method: 'POST', route: '/prompts/:id/revert/:versionId' },
    PROMPTS_RESOLVE: { type: 'ACTION', method: 'GET', route: '/prompts/by-used-in/:usedIn' },
    PROMPTS_USED_IN_VALUES: { type: 'ACTION', method: 'GET', route: '/prompts/used-in-values' },
    PROMPTS_LIST_VARIABLES: { type: 'ACTION', method: 'GET', route: '/prompts/variables/:usedIn' },
    PROMPTS_LIST_LLM_MODELS: { type: 'ACTION', method: 'GET', route: '/prompts/llm-models' },
    PROMPTS_REFRESH_LLM_MODELS: { type: 'ACTION', method: 'POST', route: '/prompts/llm-models/refresh' },

    // ── Beta Features ────────────────────────────────────────────────
    // Dark, per-feature PAGE/ACTION keys. Add them when a Beta Feature is
    // generated; the foundation ships none.
} as const satisfies Record<string, PermissionDefinition>;

export type SystemPermissionKey = keyof typeof SystemPermissions;

// Maps PAGE route → ACTION routes that belong to that page.
// Rule: if any ACTION route from a page's group is granted, the PAGE permission must also be granted.
export const PAGE_ACTION_DEPENDENCIES: Record<string, string[]> = {
    'identity-hub': [
        '/users', '/users/all', '/users/stats', '/users/:userId', '/users/:userId/access', '/users/access/batch', '/users/register',
        '/users/update', '/users/deactivate', '/users/reactivate',
        '/users/access/grant', '/users/access/revoke',
        '/users/access/bulk-grant', '/users/access/bulk-revoke', '/users/access/replace',
        '/tenants', '/tenants/:tenantId', '/tenants/provision', '/tenants/update', '/tenants/deactivate',
        '/tenants/reactivate', '/tenants/import-settings',
        '/access/by-tenant/:tenantId', '/access/by-tenant/batch', '/access/all', '/access/count', '/access/:accessId', '/access/create', '/access/update',
        '/access/delete', '/access/restore', '/access/permissions',
        '/invites', '/invites/bulk', '/invites/regenerate',
        '/invites/:inviteId/resend', '/invites/:inviteId/link', '/invites/:inviteId',
        '/audit-logs', '/audit-logs/users/:userId', '/audit-logs/access/:accessId', '/audit-logs/tenants/:tenantId',
        '/uploads/presign', '/uploads/confirm', '/uploads/stage-memory',
    ],
    'admin': [
        '/admin/events',
        '/admin/changelog',
        '/admin/changelog/sync',
        '/admin/changelog/:versionTag/regenerate',
        '/admin/changelog/:versionTag',
        '/admin/changelog/:versionTag/approve',
        '/admin/changelog/:versionTag/approval',
        '/admin/changelog/:versionTag/send',
        '/admin/changelog/recipients',
        '/admin/changelog/recipients/:recipientId',
        '/admin/changelog/settings',
        '/admin/changelog/slack-test',
        '/admin/changelog/alerts-slack-test',
        '/admin/changelog/digests',
        '/admin/changelog/digests/candidates',
        '/admin/changelog/digests/:digestId',
        '/admin/changelog/digests/:digestId/regenerate',
        '/admin/changelog/digests/:digestId/approve',
        '/admin/changelog/digests/:digestId/approval',
        '/admin/changelog/digests/:digestId/send',
        '/admin/settings/ops',
    ],
    'dashboard': [
        '/dashboard/industries',
        '/dashboard/industry-products',
        '/dashboard/competitor-options',
        '/dashboard/summary',
        '/dashboard/my-brand-chart',
        '/dashboard/competitor-chart',
        '/dashboard/my-brand-filter-options',
        '/dashboard/competitor-filter-defaults',
    ],
    'workspace': [],
    'personas': [
        '/personas/list',
        '/personas/:id',
        '/personas/:id/usage',
        '/personas/discover',
        '/personas/:id/reject',
        '/personas/status/:correlationId',
        '/personas/onboarding/status',
        '/personas/onboarding/suggestions',
        '/personas/onboarding/accept',
        '/personas/onboarding/reject',
        '/personas/onboarding/continue',
        '/personas/onboarding/undo',
    ],
    'overview': [
        '/overview/summary',
        '/overview/scenario-options',
        '/overview/subfeature-options',
        '/overview/group-by-options',
        '/overview/competitor-filter-defaults',
        '/overview/industries',
        '/overview/industry-products',
        '/overview/competitor-options',
        '/overview/topic-detail/examples',
        '/overview/topic-detail/fatigue',
        '/overview/topic-detail/comp/examples',
        '/overview/topic-detail/comp/fatigue',
    ],
    'drilldown': [
        '/drilldown/data',
        '/drilldown/creative-hash-data',
        '/drilldown/my-brand-creative-data',
        '/drilldown/my-brand-creative-performance',
        '/drilldown/my-brand-creative-fatigue',
        '/drilldown/competitor-filter-defaults',
        '/drilldown/industries',
        '/drilldown/industry-products',
        '/drilldown/competitor-options',
    ],
    'marketplace': [
        '/marketplace/vendors',
        '/marketplace/vendors/:vendorId/questions',
        '/marketplace/connections',
        '/marketplace/connections/:id/answers',
        '/marketplace/connections/:id/resign',
        '/marketplace/connections/:id',
        '/marketplace/orders',
        '/marketplace/orders/sent-scripts',
        '/marketplace/orders/:id/retry',
        '/marketplace/orders/:id/eta',
    ],
    'marketplace.admin': [
        '/marketplace/admin/vendors',
        '/marketplace/admin/vendors/:vendorId',
        '/marketplace/admin/vendors/:vendorId/activate',
        '/marketplace/admin/vendors/:vendorId/deactivate',
        '/marketplace/admin/vendors/:vendorId/contact-emails/:emailId/approve',
        '/marketplace/admin/vendors/:vendorId/contact-emails/:emailId/revoke',
        '/marketplace/admin/team-recipients',
        '/marketplace/admin/team-recipients/:recipientId',
        '/uploads/presign',
        '/uploads/confirm',
        '/uploads/stage-memory',
    ],
    'opportunities': [
        '/opportunities/list', '/opportunities/facets', '/opportunities/creatives',
        '/opportunities/generate-new-set', '/opportunities/generate', '/opportunities/reject',
        '/opportunities/export', '/opportunities/status/:correlationId', '/opportunities/cancel',
        '/opportunities/industries', '/opportunities/industry-products', '/opportunities/tenant-products',
        '/opportunities/tenant-product-types', '/opportunities/competitor-options',
        '/opportunity-sets/list', '/opportunity-sets', '/opportunity-sets/:id',
        '/opportunity-sets/:id/retry', '/opportunity-sets/minimal-generation',
        '/opportunity-sets/:id/scripts', '/opportunity-sets/:id/scripts/facets',
        '/opportunity-sets/:id/scripts/export.xlsx',
        '/opportunity-sets/:id/scripts/:scriptId/revert',
        '/opportunity-sets/:id/scripts/:scriptId/favorite',
        '/opportunity-sets/:id/scripts/:scriptId/scenes/reorder',
        '/opportunity-sets/:id/scripts/ai-edit',
        '/opportunity-sets/:id/scripts/suggest',
        '/opportunity-sets/:id/scripts/resolve-links',
        '/opportunity-sets/:id/scripts/:scriptId/storyboard',
    ],
    'persona-opportunities': [
        '/persona-opportunity-sets',
        '/persona-opportunity-sets/generate',
        '/persona-opportunity-sets/generate/query-tuning',
        '/persona-opportunity-sets/:id',
        '/persona-opportunity-sets/:id/retry',
        '/persona-opportunity-sets/:id/regenerate',
        // Persona scripts — nested under sets; reuse GENERATE/LIST (no new ACTION keys).
        '/persona-opportunity-sets/:id/scripts/generate',
        '/persona-opportunity-sets/:id/scripts/delete',
        '/persona-opportunity-sets/:id/scripts',
        '/persona-opportunity-sets/:id/opportunities/:opportunityId/scripts',
        '/persona-opportunity-sets/:id/briefs',
        '/persona-opportunity-sets/:id/opportunities/:opportunityId/briefs',
        '/persona-opportunity-sets/:id/briefs/:briefId/versions',
        '/persona-opportunity-sets/:id/briefs/:briefId/versions/:version/restore',
        '/persona-opportunity-sets/:id/briefs/:briefId/content',
        '/persona-opportunity-sets/:id/briefs/:briefId/favorite',
        '/persona-opportunity-sets/:id/briefs/ai-edit',
        '/persona-opportunity-sets/:id/briefs/delete',
        '/persona-opportunity-sets/:id/scripts/:scriptId/scene-versions',
        '/persona-opportunity-sets/:id/scripts/:scriptId/scene-versions/:version/restore',
        '/persona-opportunity-sets/:id/scripts/:scriptId/guidelines',
        '/persona-opportunity-sets/:id/scripts/:scriptId/content',
        '/persona-opportunity-sets/:id/scripts/:scriptId/favorite',
        '/persona-opportunity-sets/:id/scripts/:scriptId/hooks/suggest',
        '/persona-opportunity-sets/:id/scripts/:scriptId/hooks/confirm',
        '/persona-opportunity-sets/:id/scripts/ai-edit',
        '/persona-opportunity-sets/:id/scripts/suggest',
        '/persona-opportunity-sets/:id/scripts/resolve-links',
        '/persona-opportunity-sets/:id/scripts/:scriptId/storyboard',
        '/persona-opportunity-sets/:id/guidelines/extract',
        '/persona-opportunity-sets/:id/scripts/import',
        '/persona-opportunity-sets/:id/scripts/import/extract',
        '/persona-opportunity-sets/:id/scripts/import/legacy',
        '/persona-opportunity-sets/legacy-opportunity-sets',
        '/persona-opportunity-sets/legacy-opportunity-sets/:legacySetId/scripts',
        '/uploads/presign',
        '/uploads/confirm',
    ],
    'pre-flight': [
        '/uploads/presign',
        '/uploads/confirm',
        '/uploads/stage-memory',
        '/preflight/reports',
        '/preflight/reports/:id',
        '/preflight/reports/:id/status',
        '/preflight/reports/:id/favorite',
        '/preflight/reports/:id/retry',
        '/preflight/reports/:id/export.pdf',
        '/preflight/criteria',
        '/preflight/markets',
        '/preflight/markets/:id',
        '/preflight/insights',
        '/preflight/creative-previews',
    ],
    'pre-flight-scripts': [
        '/uploads/presign',
        '/uploads/confirm',
        '/uploads/stage-memory',
        '/script-reviews',
        '/script-reviews/:id',
        '/script-reviews/:id/status',
        '/script-reviews/:id/notes',
        '/script-reviews/:id/share',
    ],
    // Every MCP tool is listed here — MCP_ACCESS is the master switch, and the
    // tool's own key is what grants the capability. A tool missing from this
    // bundle can be granted without MCP_ACCESS, which fails closed but is
    // invisible in the role editor.
    'mcp': [
        '/mcp/tools/list_recent_persona_opportunities',
        '/mcp/tools/list_insight_slices',
        '/mcp/tools/list_filter_options',
        '/mcp/tools/get_competitive_insights',
        '/mcp/tools/get_brand_performance',
        '/mcp/tools/get_industry_overview',
        '/mcp/tools/list_personas',
    ],
    'public-api': [
        '/api-tokens',
        '/api-tokens/:id',
        '/preflight/markets',
        '/uploads/presign',
        '/uploads/confirm',
        '/preflight/reports',
        '/preflight/reports/:id/status',
        '/preflight/reports/:id',
    ],
    'prompts': [
        '/prompts',
        '/prompts/:id',
        '/prompts/:id/versions',
        '/prompts/:id/revert/:versionId',
        '/prompts/by-used-in/:usedIn',
        '/prompts/used-in-values',
        '/prompts/variables/:usedIn',
        '/prompts/llm-models',
        '/prompts/llm-models/refresh',
    ],
};
